import { useState, useCallback, useMemo } from 'react';

const getOS = () => {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod|Macintosh/.test(ua) && 'ontouchend' in document) return 'ios';
  if (/Macintosh|Mac OS X/.test(ua)) return 'mac';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
};

const IOSShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const AndroidShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const DesktopShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const NativeShareButton = ({ url, title, className = '' }) => {
  const [copied, setCopied] = useState(false);
  const os = useMemo(getOS, []);

  const ShareIcon = useMemo(() => {
    if (os === 'ios' || os === 'mac') return IOSShareIcon;
    if (os === 'android') return AndroidShareIcon;
    return DesktopShareIcon;
  }, [os]);

  const handleShare = useCallback(async () => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Final fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [url, title]);

  return (
    <button
      onClick={handleShare}
      data-testid="native-share-btn"
      className={`
        inline-flex items-center justify-center gap-2
        px-5 py-2.5 rounded-full
        text-sm font-medium
        border border-border
        bg-background hover:bg-muted
        text-foreground
        transition-all duration-200
        active:scale-95
        ${copied ? 'border-green-500/50 text-green-700 bg-green-50' : ''}
        ${className}
      `}
    >
      {copied ? <CheckIcon /> : <ShareIcon />}
      <span>{copied ? 'Link Copied!' : 'Share'}</span>
    </button>
  );
};

export default NativeShareButton;
